import React, { useEffect, useState, useRef } from "react";
import {
  Input,
  Button,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Typography,
  Popover,
  Checkbox,
  Grid,
  Autocomplete,
  Chip,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Collapse,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Print as PrintIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import {
  getTransfers as IndentListApi,
  TransferDelete as IndentDelete,
  ApproveIndent,
} from "../../../Services/Services";
import { useMediaQuery, useTheme } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { handlePrintTransfer } from "./TransferPrint";
pdfMake.vfs = pdfFonts.vfs;

const IndentList = () => {
  const [indents, setIndents] = useState([]);
  const [searchCategory, setSearchCategory] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const [searchUOM, setSearchUOM] = useState("");
  const [searchProject, setSearchProject] = useState("");
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [indentToDelete, setIndentToDelete] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [indentToApprove, setIndentToApprove] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    date_range: "today",
  });
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedTransfer, setExpandedTransfer] = useState(null);

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleReset = () => {
    setFilters({
      date_range: "today",
    });
    setSearchFrom("");
    setSearchTo("");
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
  };


  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };
  const loadIndent = async (pageNum = 1, reset = false) => {
    const data = await IndentListApi(pageNum, {
      from_project: searchFrom,
      to_project: searchTo,
      date_range: filters.date_range || "this_month",
      from_date: customDateRange.from || "",
      to_date: customDateRange.to || "",
    });

    if (reset) {
      setIndents(data.results);
    } else {
      setIndents((prev) => [...prev, ...data.results]);
    }

    setHasMore(data.next !== null);
    setPage(pageNum);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      loadIndent(1, true);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchFrom, searchTo, filters.date_range, customDateRange]);

  const fetchMoreData = () => {
    const nextPage = page + 1;
    loadIndent(nextPage, false);
  };

  const filteredIndents = indents.filter((indent) => {
    const projectMatch = searchProject
      ? indent?.to_project_name
        ?.toLowerCase()
        .includes(searchProject.toLowerCase())
      : true;

    const indentNoMatch = searchItem
      ? indent?.from_project_name
        ?.toLowerCase()
        .includes(searchItem.toLowerCase())
      : true;

    return projectMatch && indentNoMatch;
  });

  const handleClick = () => {
    navigate("/dashboard/transferform");
  };

  const handleDelete = async (Id) => {
    if (!Id) {
      console.error("No indent ID provided!");
      toast.error("Cannot delete: Indent ID is missing.");
      return;
    }

    try {
      await IndentDelete(Id);
      setIndents((prev) => prev.filter((indent) => indent.id !== Id));
      toast.success("Indent deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete indent. Please try again.");
    }
  };

  const handleEdit = (indent) => {
    navigate("/dashboard/TransferForm", {
      state: {
        data: indent,
      },
    });
  };

function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";

  // Take only the date part (before T)
  const datePart = dateTimeString.split("T")[0];  // "2025-12-02"

  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
}

  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);

  return (
    <Box>
      <Box>
        {/* ✅ MOBILE/DESKTOP RESPONSIVE FILTER SECTION */}
        <Box
          sx={{
            mt: { xs: 0, sm: -2 },
            pt: 0,
            mb: 2,
            px: 1,
          }}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
            {/* Date Range Filter */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="Date Range"
                value={
                  filters.date_range === "custom" &&
                    customDateRange.from &&
                    customDateRange.to
                    ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                    : filters.date_range === "today"
                      ? "Today"
                      : filters.date_range === "yesterday"
                        ? "Yesterday"
                        : filters.date_range === "this_week"
                          ? "This Week"
                          : filters.date_range === "previous_week"
                            ? "Previous Week"
                            : filters.date_range === "this_month"
                              ? "This Month"
                              : filters.date_range === "current_year"
                                ? "Current Year"
                                : filters.date_range === "financial_year"
                                  ? "Financial Year"
                                  : "Select Date Range"
                }
                onClick={(e) => setDateMenuAnchor(e.currentTarget)}
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton size="small" edge="end">
                      <ArrowDropDownIcon />
                    </IconButton>
                  ),
                }}
                sx={{
                  cursor: "pointer",
                  "& .MuiInputBase-input": {
                    fontSize: "0.8rem",
                    padding: "4px 8px",
                  },
                  "& .MuiInputBase-root": {
                    height: 30,
                    borderRadius: 0,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                  },
                }}
              />
            </Grid>

            {/* Search From Project */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search By From Project"
                variant="outlined"
                size="small"
                fullWidth
                value={searchFrom}
                onChange={(e) => setSearchFrom(e.target.value)}
                InputProps={{
                  sx: {
                    height: "30px",
                    borderRadius: 0,
                    fontSize: "0.7rem",
                    padding: "0 8px",
                  },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            {/* Search To Project */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search By To Project"
                variant="outlined"
                size="small"
                fullWidth
                value={searchTo}
                onChange={(e) => setSearchTo(e.target.value)}
                InputProps={{
                  sx: {
                    height: "30px",
                    fontSize: "0.7rem",
                    padding: "0 8px",
                    borderRadius: 0,
                  },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            {/* Reset Button */}
            <Grid item xs={6} sm={3} md={1.5}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleReset}
                fullWidth
                sx={{
                  height: "30px",
                  whiteSpace: "nowrap",
                  borderRadius: "0",
                  fontSize: "0.7rem",
                }}
              >
                Reset
              </Button>
            </Grid>

            {/* Transfer Button */}
            <Grid item xs={6} sm={3} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
                startIcon={<AddIcon fontSize="small" />}
                fullWidth
                sx={{
                  height: "30px",
                  fontSize: "0.7rem",
                  px: 2,
                  whiteSpace: "nowrap",
                  borderRadius: 0,
                }}
              >
                Transfer
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Date Range Menu - UPDATED */}
        <Menu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => {
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
            setTempCustomDateRange({ from: "", to: "" });
          }}
          PaperProps={{
            sx: {
              width: 350,
              maxHeight: 500,
              "& .MuiTypography-root": {
                fontSize: "0.65rem",
              },
            },
          }}
        >
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "this_week", label: "This Week" },
            { key: "previous_week", label: "Previous Week" },
            { key: "this_month", label: "This Month" },
            { key: "current_year", label: "Current Year" },
            { key: "financial_year", label: "Financial Year" },
          ].map((range) => (
            <MenuItem
              key={range.key}
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: range.key }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">{range.label}</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay(range.key)}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          <Divider />

          {/* ✅ Custom Date Range - Expandable */}
          <MenuItem
            onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
              Custom Date Range
            </Typography>
            {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>

          {/* ✅ Both Date Pickers Together */}
          <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Select Date Range
              </Typography>

              {/* From and To Date Inputs Side by Side */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.from}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: tempCustomDateRange.to || new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.to}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempCustomDateRange.from && tempCustomDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                  {formatDateDisplay(tempCustomDateRange.to)}
                </Typography>
              )}

              {/* Apply and Cancel Buttons */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCustomDateRange}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Menu>
      </Box>

      {/* ✅ INFINITE SCROLL WITH MOBILE/DESKTOP VIEWS */}
      <Box id="print-section">
        <InfiniteScroll
          dataLength={indents.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
        // height={"80vh"}
        >
          {isMobile ? (
            /* 📱 MOBILE VIEW - Expandable Cards */
            <Box sx={{ bgcolor: "background.paper" }}>
              {filteredIndents.map((indent, index) => (
                <Box key={indent.id || index}>
                  {/* Main Row - Always Visible */}
                  <Box
                    onClick={() =>
                      setExpandedTransfer(
                        expandedTransfer === indent.id ? null : indent.id
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:active": {
                        bgcolor: "#e3f2fd",
                      },
                      borderLeft: "4px solid #9c27b0",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      {/* Transfer Number */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#666",
                            minWidth: 25,
                          }}
                        >
                          #{index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                          }}
                        >
                          {indent.transfer_no || "N/A"}
                        </Typography>
                      </Box>

                      {/* From → To Project */}
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {indent.from_project_name?.substring(0, 15) || "N/A"}
                        </span>
                        <span style={{ color: "#9c27b0", fontWeight: "bold" }}>
                          →
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {indent.to_project_name?.substring(0, 15) || "N/A"}
                        </span>
                      </Typography>
                    </Box>

                    {/* Expand Icon */}
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedTransfer === indent.id ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Expanded Details */}
                  <Collapse
                    in={expandedTransfer === indent.id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                        borderLeft: "4px solid #9c27b0",
                      }}
                    >
                      {/* Details Grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr",
                          gap: 0.8,
                          mb: 1,
                        }}
                      >
                        {/* From Project - Full */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            FROM PROJECT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#333",
                            }}
                          >
                            {indent.from_project_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* To Project - Full */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            TO PROJECT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#333",
                            }}
                          >
                            {indent.to_project_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Created By & Date */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 0.8,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              CREATED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {indent.created_by_name || "N/A"}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                mt: 0.5,
                              }}
                            >
                              SLIP NO
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {indent.slip_number || "N/A"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              DATE
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {formatOnlyDate(indent.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 0.6,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(indent);
                          }}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#bbdefb",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <EditIcon
                            sx={{ fontSize: "1rem", color: "#1976d2" }}
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintTransfer(indent);
                          }}
                          sx={{
                            bgcolor: "#f3e5f5",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#e1bee7",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <PrintIcon
                            sx={{ fontSize: "1rem", color: "#7b1fa2" }}
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setIndentToDelete(indent.id);
                          }}
                          disabled={indent.status === true}
                          sx={{
                            bgcolor: "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                            "&:disabled": {
                              bgcolor: "#f5f5f5",
                              opacity: 0.5,
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "1rem", color: "#d32f2f" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Collapse>

                  {/* Divider between items */}
                  <Divider />
                </Box>
              ))}
            </Box>
          ) : (
            /* 💻 DESKTOP VIEW - Table */
            <TableContainer
              component={Paper}
              sx={{
                overflow: "auto",
                maxHeight: "88vh"
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1200,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid #ccc",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        backgroundColor: "#f5f5f5",
                        fontSize: "0.7rem",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                        minWidth: 80,
                      }}
                    >
                      S. No.
                    </TableCell>
                    <TableCell>Transfer No.</TableCell>
                    <TableCell>Slip No.</TableCell>
                    <TableCell>From Project</TableCell>
                    <TableCell>To Project</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredIndents.map((indent, index) => (
                    <TableRow
                      hover
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 3,
                          minWidth: 80,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{indent.transfer_no}</TableCell>
                      <TableCell>{indent.slip_number}</TableCell>
                      <TableCell>{indent.from_project_name}</TableCell>
                      <TableCell>{indent.to_project_name}</TableCell>
                      <TableCell>{indent.created_by_name}</TableCell>
                      <TableCell>{formatOnlyDate(indent.created_at)}</TableCell>

                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 2,
                        }}
                      >
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={() => handleEdit(indent)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={() => handlePrintTransfer(indent)}
                          size="small"
                        >
                          <PrintIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setIndentToDelete(indent.id);
                          }}
                          disabled={indent.status === true}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>

        <ToastContainer position="top-right" autoClose={3000} />

        {/* Delete Confirmation Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setIndentToDelete(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="body2">Are you sure?</Typography>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
              <Button
                color="error"
                size="small"
                onClick={() => {
                  if (indentToDelete) {
                    handleDelete(indentToDelete);
                    setAnchorEl(null);
                    setIndentToDelete(null);
                  } else {
                    toast.error("Indent ID is missing.");
                  }
                }}
              >
                Yes
              </Button>

              <Button
                size="small"
                onClick={() => {
                  setAnchorEl(null);
                  setIndentToDelete(null);
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default IndentList;